import { useCallback, useMemo, useState } from 'react';
import { FormGroup, H3, NumericInput } from '@blueprintjs/core';
import Unit from '../Unit';
import { WorkflowMeasurements } from '../../types';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { WavelightWorkflowState } from '../../views/workflow/types';
import {
  setMeasurementsData,
  setRequestId,
  uploadData,
} from '../../views/workflow/reducer';
import {
  WorkflowFooter,
  WorkflowInnerContainer,
} from '../../views/workflow/pages/styles';
import { PreviousButton } from '../FooterButtons';
import AuthButton from '../AuthButton';
import FieldsUnfilledAlert from '../FieldsUnfilledAlert';
import { checkUnfilledFields } from '../../utils';
import { PressureFieldsContainer } from './styles';

const Measurements = () => {
  const dispatch = useAppDispatch();
  const {
    data,
    requestId,
    isUploading,
    videoUploadError,
  }: WavelightWorkflowState = useAppSelector((state) => state.workflow);

  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);

  const onChange = useCallback(
    (partial: Partial<WorkflowMeasurements>) => {
      dispatch(
        setMeasurementsData({
          ...data.measurements,
          ...partial,
        }),
      );
    },
    [dispatch, data.measurements],
  );

  const sendData = useCallback(() => {
    if (!!requestId) return;

    const res = dispatch(uploadData());
    dispatch(setRequestId(res.requestId));
  }, [dispatch, requestId]);

  const unfilledFields = useMemo(() => checkUnfilledFields(data), [data]);

  const handleVerifyBeforeSend = useCallback(() => {
    if (unfilledFields?.length) {
      setIsAlertOpen(true);
    } else {
      sendData();
    }
  }, [sendData, unfilledFields]);

  return (
    <>
      <WorkflowInnerContainer>
        <H3>3. Medidas</H3>
        <FormGroup label={'Batimentos Cardíacos'} labelFor="bpm-input">
          <NumericInput
            inputMode="numeric"
            selectAllOnFocus
            fill
            id="bpm-input"
            leftIcon="pulse"
            value={data.measurements.bpm}
            onValueChange={(value: number) => onChange({ bpm: value })}
            stepSize={1}
            max={200}
            clampValueOnBlur
            rightElement={<Unit>bpm</Unit>}
            buttonPosition="none"
          />
        </FormGroup>
        <FormGroup label={'Saturação'} labelFor="saturation-input">
          <NumericInput
            inputMode="numeric"
            selectAllOnFocus
            fill
            id="saturation-input"
            leftIcon="waves"
            value={data.measurements.bloodO2Saturation}
            onValueChange={(value: number) =>
              onChange({ bloodO2Saturation: value })
            }
            rightElement={<Unit>%</Unit>}
            buttonPosition="none"
            min={0}
            max={100}
            clampValueOnBlur
          />
        </FormGroup>
        <FormGroup label={'Pressão'}>
          <PressureFieldsContainer>
            <FormGroup label={'Sistólica'} labelFor="preassure-sistole-input">
              <NumericInput
                inputMode="numeric"
                selectAllOnFocus
                fill
                id="preassure-sistole-input"
                leftIcon="heart"
                value={data.measurements.bloodPreassure.systolic}
                onValueChange={(value: number) =>
                  onChange({
                    bloodPreassure: {
                      systolic: value,
                      diastolic: data.measurements.bloodPreassure.diastolic,
                    },
                  })
                }
                rightElement={<Unit>mmHg</Unit>}
                buttonPosition="none"
                min={0}
                max={300}
                clampValueOnBlur
              />
            </FormGroup>
            <FormGroup label={'Diastólica'} labelFor="preassure-diastole-input">
              <NumericInput
                inputMode="numeric"
                selectAllOnFocus
                fill
                id="preassure-diastole-input"
                leftIcon="heart"
                value={data.measurements.bloodPreassure.diastolic}
                onValueChange={(value: number) =>
                  onChange({
                    bloodPreassure: {
                      diastolic: value,
                      systolic: data.measurements.bloodPreassure.systolic,
                    },
                  })
                }
                rightElement={<Unit>mmHg</Unit>}
                buttonPosition="none"
                min={0}
                max={300}
                clampValueOnBlur
              />
            </FormGroup>
          </PressureFieldsContainer>
        </FormGroup>
      </WorkflowInnerContainer>
      <WorkflowFooter>
        <PreviousButton />
        <AuthButton
          onClick={handleVerifyBeforeSend}
          disabled={isUploading || videoUploadError}
        >
          Enviar
        </AuthButton>
      </WorkflowFooter>
      <FieldsUnfilledAlert
        isOpen={isAlertOpen}
        unfilledFields={unfilledFields}
        onClose={() => setIsAlertOpen(false)}
      />
    </>
  );
};

export default Measurements;
